import IdentificationTypeSelect from '@/components/common/input/identification-types-select/IdentificationTypeSelect';
import CalendarModal from '@/components/common/modals/calendar-modal/CalendarModal';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import formEventoWingfoilWindsurfareaSchema from '@/schemas/form-evento-wingfoil-windsurfarea';
import { useRootStore } from '@/stores/root-store';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormEventoWingfoilWindsurfareaProps, {
  TForm
} from './FormEventoWingfoilWindsurfarea.types';
import { Button } from '@/components/ui/button';
import { Loader } from 'lucide-react';

const EventoWingfoilWindsurfarea: React.FC<
  FormEventoWingfoilWindsurfareaProps
> = ({ onSubmit, isPending }) => {
  const { t } = useTranslation();
  const stepServicesData = useRootStore((store) => store.stepServicesData);
  const removeStep = useRootStore((store) => store.removeStep);
  const currentStep = useRootStore((store) => store.currentStep);
  const minAge = stepServicesData?.service?.minAge;
  const maxAge = stepServicesData?.service?.maxAge;
  const form = useForm<TForm>({
    resolver: zodResolver(
      formEventoWingfoilWindsurfareaSchema({ minAge, maxAge })
    ),
    defaultValues: {
      userIsParticipant: true,
      user: {
        firstName: '',
        lastName: '',
        phone: '',
        identification: {
          number: '',
          type: ''
        },
        email: '',
        address: '',
        location: '',
        postalCode: '',
        birthdate: undefined,
        illness: {
          value: false,
          answer: ''
        }
      },
      emergencyContact: {
        fullName: '',
        phone: ''
      },
      participant: {
        firstName: '',
        lastName: '',
        identification: {
          number: '',
          type: ''
        },
        address: '',
        location: '',
        postalCode: '',
        birthdate: undefined,
        illness: {
          value: false,
          answer: ''
        }
      },
      acceptDataPrivacy: false,
      acceptTakePhotos: false,
      authOfferProducts: false
    },
    mode: 'onChange',
    shouldFocusError: true
  });
  const formError = Boolean(Object.keys(form.formState.errors).length);

  const userIsParticipant = form.watch('userIsParticipant');

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-8"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-8">
          {/* CHECKBOX SI PARTICIPANTE ES MENOR */}

          <FormField
            control={form.control}
            name="userIsParticipant"
            render={({ field }) => (
              <FormItem>
                <div className="p-2 space-y-3">
                  <FormControl>
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="userIsParticipant"
                        checked={!field.value}
                        onCheckedChange={() => {
                          form.clearErrors();
                          field.onChange(!field.value);
                        }}
                        className="w-5 h-5"
                      />
                      <FormLabel
                        htmlFor="userIsParticipant"
                        className="text-base font-bold"
                      >
                        {t('stepParticipants.userIsParticipantAndUnderage')}
                      </FormLabel>
                    </div>
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          {/* USER: Datos del padre/madre/tutor (si no es participante) */}

          <div className="p-6 space-y-4 border rounded-md border-primary">
            <h3 className="font-bold">
              {userIsParticipant
                ? t('stepParticipants.titleStudent')
                : t('stepParticipants.titleParent')}
            </h3>

            {/* Nombre */}
            <FormField
              control={form.control}
              name="user.firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stepParticipants.form.firstName')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Apellido */}
            <FormField
              control={form.control}
              name="user.lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Teléfono */}
            <FormField
              control={form.control}
              name="user.phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('phone')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="user.identification.type"
              render={({ field }) => {
                return (
                  <FormItem>
                    <IdentificationTypeSelect
                      defaultValue={field?.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                      }}
                      field={undefined}
                    />
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="user.identification.number"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>
                      {t('stepParticipants.form.identificationNumber')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('stepParticipants.form.id')}
                        {...field}
                        value={field?.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="user.email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('stepParticipants.form.mail')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormDescription>
                    {t('stepParticipants.form.mail_description')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {userIsParticipant && (
              <>
                {/* Fecha de Nacimiento */}
                <FormField
                  control={form.control}
                  name="user.birthdate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('stepParticipants.form.birth')}</FormLabel>
                      <CalendarModal
                        value={field.value}
                        placeholder={t('stepParticipants.form.birth')}
                        onAccept={(value) => {
                          field.onChange(value);
                        }}
                        initialFocus
                        dropDownPicker
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Domicilio */}
                <FormField
                  control={form.control}
                  name="user.address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.address')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Localidad */}
                <FormField
                  control={form.control}
                  name="user.location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.location')}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Madrid" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* CP */}
                <FormField
                  control={form.control}
                  name="user.postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.postalCode')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Enfermedad */}

                <FormField
                  control={form.control}
                  name="user.illness"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <div className="flex flex-row items-end gap-5">
                          <FormLabel>{t('stepParticipants.illness')}</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={(value) =>
                                field.onChange({
                                  ...field.value,
                                  value: value === 'yes'
                                })
                              }
                              defaultValue=""
                              className="flex items-center mt-0 space-x-2"
                            >
                              <FormItem className="flex items-center space-x-1 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="yes" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {t('common.yes')}
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-1 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="no" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {t('common.no')}
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                        </div>

                        {field?.value?.value && (
                          <div className="py-1">
                            <Textarea
                              placeholder={t('newBooking.extraInfo')}
                              {...field}
                              onChange={(e) =>
                                field.onChange({
                                  ...field.value,
                                  answer: e.target.value
                                })
                              }
                              value={field?.value?.answer ?? ''}
                            />
                          </div>
                        )}
                      </FormItem>
                    );
                  }}
                />
              </>
            )}
          </div>

          {/* Datos del participante */}

          {!userIsParticipant && (
            <div className="p-6 space-y-4 border rounded-md border-primary">
              <h3 className="font-bold">
                {t('stepParticipants.titleStudent')}
              </h3>

              {/* Nombre */}
              <FormField
                control={form.control}
                name="participant.firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('stepParticipants.form.firstName')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Introduce el nombre del alumno"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Primer Apellido */}
              <FormField
                control={form.control}
                name="participant.lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Introduce el apellido del alumno"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="participant.identification.type"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <IdentificationTypeSelect
                        defaultValue={field?.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                        }}
                        field={undefined}
                      />
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="participant.identification.number"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>
                        {t('stepParticipants.form.identificationNumber')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('stepParticipants.form.id')}
                          {...field}
                          value={field?.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {/* Fecha de Nacimiento */}
              <FormField
                control={form.control}
                name="participant.birthdate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('stepParticipants.form.birth')}</FormLabel>
                    <CalendarModal
                      value={field.value}
                      placeholder={t('stepParticipants.form.birth')}
                      onAccept={(value) => {
                        field.onChange(value);
                      }}
                      initialFocus
                      dropDownPicker
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Domicilio */}
              <FormField
                control={form.control}
                name="participant.address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.address')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Localidad */}
              <FormField
                control={form.control}
                name="participant.location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.location')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Madrid" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* CP */}
              <FormField
                control={form.control}
                name="participant.postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.postalCode')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Enfermedad */}
              <FormField
                control={form.control}
                name="participant.illness"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-end gap-5">
                        <FormLabel>{t('stepParticipants.illness')}</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) =>
                              field.onChange({
                                ...field.value,
                                value: value === 'yes'
                              })
                            }
                            defaultValue=""
                            className="flex items-center mt-0 space-x-2"
                          >
                            <FormItem className="flex items-center space-x-1 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="yes" />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {t('common.yes')}
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-1 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="no" />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {t('common.no')}
                              </FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                      </div>

                      {field?.value?.value && (
                        <div className="px-2 py-1">
                          <Textarea
                            placeholder={t('newBooking.extraInfo')}
                            {...field}
                            onChange={(e) =>
                              field.onChange({
                                ...field.value,
                                answer: e.target.value
                              })
                            }
                            value={field?.value?.answer ?? ''}
                          />
                        </div>
                      )}
                    </FormItem>
                  );
                }}
              />
            </div>
          )}

          {/* Persona de contacto */}

          <div className="p-6 space-y-4 border rounded-md border-primary">
            <h3 className="font-bold">
              {t('stepParticipants.additionalContact')}
            </h3>

            {/* Nombre y Apellidos */}
            <FormField
              control={form.control}
              name="emergencyContact.fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {`${t('stepParticipants.form.firstName')} y ${t(
                      'stepParticipants.form.lastName'
                    ).toLowerCase()}`}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Teléfono */}

            <FormField
              control={form.control}
              name="emergencyContact.phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('phone')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Privacidad de datos  */}
          <FormField
            control={form.control}
            name="acceptDataPrivacy"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-start">
                  <FormControl>
                    <Checkbox
                      id="isAcceptDataPrivacy"
                      checked={field.value}
                      onCheckedChange={() => field.onChange(!field.value)}
                    />
                  </FormControl>
                  <FormLabel htmlFor="isAcceptDataPrivacy" className="ml-2">
                    {t('stepParticipants.form.privacyPolicy')}{' '}
                    <a
                      href="https://windsurfarea.com/politica-privacidad/"
                      target="blank"
                      className="underline text-primary"
                      rel="noreferrer noopener"
                    >
                      {t('stepParticipants.form.privacyPolicyLink')}
                    </a>
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Fotografía  */}
          <FormField
            control={form.control}
            name="acceptTakePhotos"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-start">
                  <FormControl>
                    <Checkbox
                      id="isAcceptTakePhotos"
                      checked={field.value}
                      onCheckedChange={() => field.onChange(!field.value)}
                    />
                  </FormControl>
                  <FormLabel htmlFor="isAcceptTakePhotos" className="ml-2">
                    {t('stepParticipants.form.takePhotos')}
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Ofrecimiento de productos/servicios  */}
          <FormField
            control={form.control}
            name="authOfferProducts"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-start">
                  <FormControl>
                    <Checkbox
                      id="isAuthOfferProducts"
                      checked={field.value}
                      onCheckedChange={() => field.onChange(!field.value)}
                    />
                  </FormControl>
                  <FormLabel htmlFor="isAuthOfferProducts" className="ml-2">
                    {t('stepParticipants.form.acceptOffers')}
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          {formError && (
            <p className="text-sm font-medium text-destructive">
              {t('newBooking.formParticipantData.errorMessage')}
            </p>
          )}

          <div className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <Loader className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                t('newBooking.continue')
              )}
            </Button>
            <Button
              disabled={isPending}
              onClick={() => {
                removeStep(currentStep);
              }}
              variant={'outline'}
            >
              {t('newBooking.goBack')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default EventoWingfoilWindsurfarea;
